/**
 * User Service - User profile and admin operations
 */

import { doc, getDoc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { db, auth } from "@/config/firebase";
import type { User } from "@/types";

/**
 * Get current user's profile from Firestore
 */
export async function getCurrentUserProfile(): Promise<User | null> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }

  const docRef = doc(db, "users", currentUser.uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as User;
  }

  return null;
}

/**
 * Check if current user is an admin
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  const profile = await getCurrentUserProfile();
  return profile?.isAdmin === true;
}

/**
 * Subscribe to user's admin status changes
 * @param callback - Called with admin status when it changes
 * @param userId - Optional user ID. If not provided, uses auth.currentUser
 */
export function subscribeToAdminStatus(
  callback: (isAdmin: boolean) => void,
  userId?: string
): Unsubscribe | null {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) {
    callback(false);
    return null;
  }

  const docRef = doc(db, "users", uid);

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data() as User;
      callback(data.isAdmin === true);
    } else {
      callback(false);
    }
  });
}

/**
 * Get user profile by ID
 */
export async function getUserProfile(userId: string): Promise<User | null> {
  const docRef = doc(db, "users", userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as User;
  }

  return null;
}
